<script setup lang="ts" generic="TValue extends string | number = string | number">
/**
 * UI-kit component
 * Category: Selects
 * Name: 'AppMultiSelect'
 */
import type { AppMultiSelectProps } from '../types/interfaces'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { AppDropdown, AppDropdownItem } from '@shared/ui/dropdowns'

import { useAppSelectClasses } from '../../app-select'
import { useAppMultiSelect } from '../composables/use-app-multi-select.hook'

defineOptions({
  name: 'AppMultiSelect',
})

const props = withDefaults(defineProps<AppMultiSelectProps<TValue>>(), {
  placeholder: '',
  label: undefined,
  optionalText: undefined,
  required: false,
  state: 'static',
  disabled: false,
  message: undefined,
  prependIcon: undefined,
  panelMinWidth: undefined,
  clearable: false,
})

const emit = defineEmits<{
  open: []
  close: []
}>()

// `update:modelValue` is auto-emitted by `defineModel`.
const modelValue = defineModel<TValue[]>()

defineSlots<{
  'prepend-icon'?: () => unknown
  option?: (scope: {
    option: AppSelectOption<TValue>
    selected: boolean
    disabled: boolean
  }) => unknown
}>()

const {
  triggerRef,
  inputRef,
  panelRef,
  isOpen,
  panelId,
  labelId,
  floatingStyles,
  isDisabled,
  isError,
  isSuccess,
  isHighlighted,
  displayedOptions,
  inputValue,
  inputPlaceholder,
  showClearValueButton,
  isOptionSelected,
  isAllSelected,
  isSelectAllDisabled,
  toggleSelectAll,
  focusInput,
  handleChevronClick,
  handleClearValueClick,
  handleInputFocus,
  handleTriggerKeydown,
  handleSelect,
} = useAppMultiSelect<TValue>({
  modelValue,
  props,
  onOpen: () => emit('open'),
  onClose: () => emit('close'),
})

// Default-skin visuals — view layer, not the headless core.
const { rootClasses, triggerClasses, messageColor, chevronIcon } = useAppSelectClasses({
  isOpen,
  isDisabled,
  isError,
  isSuccess,
})
</script>

<template>
  <div :class="rootClasses">
    <div
      v-if="label"
      class="app-select__label-row"
    >
      <AppText
        :id="labelId"
        name="body2"
        color="slate-900"
        class="app-select__label"
      >
        {{ label }}<span
          v-if="required"
          class="app-select__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalText"
        name="body2"
        color="slate-400"
        class="app-select__optional"
      >
        {{ optionalText }}
      </AppText>
    </div>

    <div
      ref="triggerRef"
      :class="triggerClasses"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-controls="panelId"
      @click="focusInput"
    >
      <span
        v-if="$slots['prepend-icon'] || prependIcon"
        class="app-select__prepend"
      >
        <slot name="prepend-icon">
          <AppIcon
            v-if="prependIcon"
            :icon="prependIcon"
          />
        </slot>
      </span>

      <input
        ref="inputRef"
        :value="inputValue"
        type="text"
        class="app-select__input"
        :placeholder="inputPlaceholder"
        :disabled="isDisabled"
        readonly
        :aria-labelledby="label ? labelId : undefined"
        :aria-required="required || undefined"
        :aria-invalid="isError || undefined"
        @focus="handleInputFocus"
        @keydown="handleTriggerKeydown"
      >

      <button
        v-if="showClearValueButton"
        type="button"
        class="app-select__clear-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Clear selection"
        @mousedown.prevent
        @click.stop="handleClearValueClick"
      >
        <AppIcon :icon="faXmark" />
      </button>
      <button
        v-else
        type="button"
        class="app-select__chevron-btn"
        :disabled="isDisabled"
        tabindex="-1"
        aria-label="Toggle dropdown"
        @mousedown.prevent
        @click.stop="handleChevronClick"
      >
        <AppIcon :icon="chevronIcon" />
      </button>
    </div>

    <AppText
      v-if="message"
      name="body2"
      :color="messageColor"
      class="app-select__message"
    >
      {{ message }}
    </AppText>

    <Teleport to="body">
      <div
        v-if="isOpen"
        :id="panelId"
        ref="panelRef"
        role="listbox"
        aria-multiselectable="true"
        class="app-select__panel"
        :style="floatingStyles"
      >
        <AppDropdown>
          <div
            v-if="displayedOptions.length > 0"
            :class="[
              'app-select__option',
              'app-select__option--select-all',
              {
                'app-select__option--selected': isAllSelected,
                'app-select__option--disabled': isSelectAllDisabled,
              },
            ]"
            role="option"
            :aria-selected="isAllSelected"
            :aria-disabled="isSelectAllDisabled || undefined"
            tabindex="-1"
            @click.stop="toggleSelectAll"
          >
            <AppDropdownItem
              tag="div"
              text="All"
              :selected="isAllSelected"
              :disabled="isSelectAllDisabled"
              :checkbox="true"
              tabindex="-1"
            />
          </div>

          <div
            v-for="(option, index) in displayedOptions"
            :key="String(option.value)"
            :data-app-select-option="index"
            :class="[
              'app-select__option',
              {
                'app-select__option--highlighted': isHighlighted(index),
                'app-select__option--selected': isOptionSelected(option as AppSelectOption<TValue>),
                'app-select__option--disabled': option.disabled === true,
              },
            ]"
            role="option"
            :aria-selected="isOptionSelected(option as AppSelectOption<TValue>)"
            :aria-disabled="option.disabled || undefined"
            tabindex="-1"
            @click="handleSelect(option as AppSelectOption<TValue>)"
          >
            <slot
              name="option"
              :option="option"
              :selected="isOptionSelected(option as AppSelectOption<TValue>)"
              :disabled="option.disabled === true"
            >
              <AppDropdownItem
                tag="div"
                :text="option.text"
                :selected="isOptionSelected(option as AppSelectOption<TValue>)"
                :disabled="option.disabled"
                :checkbox="true"
                tabindex="-1"
              />
            </slot>
          </div>
        </AppDropdown>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss" src="../styles/_app-multi-select.scss" />
